import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useSearchParams, type Location } from 'react-router-dom';
import { LoadingButton } from '@/components/ui/loading-button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import MarketingPane from '@/components/MarketingPane';

const Login: React.FC = () => {
  const { loginWithRedirect, isLoading, error } = useAuth0();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Where to land after auth: the route the guard bounced us from
  // (ProtectedRoute's `state.from`), or the `?returnTo=` the API SDK sets on
  // session expiry. Rides through Auth0 via appState; applied (and sanitized
  // to an internal path) by onRedirectCallback in main.tsx.
  const from = (location.state as { from?: Location } | null)?.from;
  const returnTo = from
    ? `${from.pathname}${from.search ?? ''}`
    : searchParams.get('returnTo') ?? undefined;

  const handleLogin = async () => {
    if (isLoading) return;
    try {
      await loginWithRedirect({
        appState: returnTo ? { returnTo } : undefined,
      });
    } catch (err) {
      console.error('Auth0 login error:', err);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[3fr_2fr]">
      <MarketingPane />

      {/* Login pane — dark, sign-in only. */}
      <main className="bg-foreground text-background flex flex-col items-center justify-center p-10 md:p-12">
        <div className="w-full max-w-xs flex flex-col items-center gap-8">
          <div className="flex flex-col items-center gap-2 text-center">
            <h2 className="text-2xl font-medium tracking-tight">Welcome back</h2>
            <p className="text-sm text-background/60">
              Sign in to manage your landings.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="w-full">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          <LoadingButton
            variant="secondary"
            size="cta"
            onClick={handleLogin}
            loading={isLoading}
            loadingText="Signing in…"
            className="w-full hover:bg-surface hover:text-foreground hover:opacity-90 active:bg-surface active:text-foreground active:opacity-75"
          >
            Sign in
          </LoadingButton>
        </div>
      </main>
    </div>
  );
};

export default Login;
